import {
  Box,
  Collapse,
  Link,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useEffect, useMemo, useState } from "react";

import { marketingPalette } from "@/components/marketing/tokens";
import type { Heading } from "@/content/types";

/**
 * The in-article contents rail, from `marketing/design/blog-spec.md`: a 15px
 * title in `primary.dark` with 1px tracking, then 16px links padded 12px in and
 * 6.4px down, 16px below the title.
 *
 * The published site builds this with a third-party script after load. Here the
 * headings already carry their anchors from `src/content/headings.ts`, so it
 * renders server-side and works without JavaScript.
 */
const TITLE = "IN THIS ARTICLE";
/** Half the viewport: an entry lights up as its heading reaches the midline. */
const SPY_ROOT_MARGIN = "0px 0px -50% 0px";
/** The published rail eases every property, not just the two that change. */
const SPY_TRANSITION = "all 0.1s cubic-bezier(0.215, 0.61, 0.355, 1)";

/**
 * The sub-entries grow rather than appear.
 *
 * The published rail declares `transition-duration: 0s` on them, a script
 * animates the height instead, so there is no CSS value to copy. Sampled every
 * 30ms it runs 0 -> 17.5 -> 27.4 -> 31.8 -> 33.3 and settles by ~180ms: a
 * pronounced ease-out. The curve is the one the site declares on the rail's own
 * links, rather than a fourth easing invented for this.
 */
const EXPAND_MS = 200;
const EXPAND_EASING = "cubic-bezier(0.215, 0.61, 0.355, 1)";

type Section = { heading: Heading; children: Heading[] };

/**
 * Both `h3` and `h4` hide under the `h2` above them, the published rail gives
 * the article two tiers, not three. A sub-heading before any `h2` has no parent
 * to hide under, so it stays a top-level row rather than being dropped.
 */
function toSections(headings: Heading[]): Section[] {
  const sections: Section[] = [];

  for (const heading of headings) {
    const previous = sections.at(-1);
    if (heading.level > 2 && previous?.heading.level === 2) {
      previous.children.push(heading);
    } else {
      sections.push({ heading, children: [] });
    }
  }

  return sections;
}

function useActiveHeadingId(headings: Heading[]): string | null {
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") {
      return;
    }

    const targets = headings
      .map((heading) => document.getElementById(heading.id))
      .filter((target): target is HTMLElement => target !== null);

    if (targets.length === 0) {
      return;
    }

    // The published rail latches: an entry stays lit after its heading has
    // scrolled off the top, until the next one reaches the line. So this reads
    // every target on each callback rather than trusting the entries that
    // changed, which only describe the crossing that woke the observer.
    const settle = () => {
      const line = window.innerHeight / 2;
      let reached: string | null = null;
      for (const target of targets) {
        if (target.getBoundingClientRect().top <= line) {
          reached = target.id;
        }
      }
      setActiveId(reached);
    };

    const observer = new IntersectionObserver(settle, {
      rootMargin: SPY_ROOT_MARGIN,
      threshold: 0,
    });
    for (const target of targets) {
      observer.observe(target);
    }

    return () => observer.disconnect();
  }, [headings]);

  return activeId;
}

/** The published chevron: 14px and stroked, where MUI's is larger and filled. */
function Chevron() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      style={{ flexShrink: 0 }}
    >
      <path
        d="M5.25 3.5L8.75 7L5.25 10.5"
        stroke="currentcolor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SectionLink({
  heading,
  active,
}: {
  heading: Heading;
  active: boolean;
}) {
  return (
    <Link
      href={`#${heading.id}`}
      underline="none"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        py: "6.4px",
        px: "12px",
        fontSize: 16,
        lineHeight: "24px",
        transition: SPY_TRANSITION,
        color: active ? "primary.dark" : "text.primary",
        ...(active
          ? {
              backgroundColor: (theme) =>
                alpha(theme.palette.primary.main, 0.1),
              borderRadius: "8px",
            }
          : { "&:hover": { backgroundColor: marketingPalette.surface } }),
      }}
    >
      <Chevron />
      <Box component="span">{heading.text}</Box>
    </Link>
  );
}

function SubsectionLink({
  heading,
  active,
}: {
  heading: Heading;
  active: boolean;
}) {
  return (
    <Link
      href={`#${heading.id}`}
      underline="none"
      sx={{
        display: "flex",
        alignItems: "center",
        p: "6.4px",
        fontSize: 14,
        lineHeight: "21px",
        letterSpacing: "0.4px",
        transition: SPY_TRANSITION,
        color: active ? "primary.dark" : alpha(marketingPalette.inkMuted, 0.9),
        ...(active
          ? { fontWeight: 500, borderRadius: "8px" }
          : { "&:hover": { backgroundColor: marketingPalette.surface } }),
      }}
    >
      {heading.text}
    </Link>
  );
}

/**
 * The rail and the sheet are the same list. Below 992 the published page
 * re-purposes the one node into a fixed bottom bar, so this takes a variant
 * rather than growing a second copy of the list that would drift from it.
 */
type ContentsVariant = "rail" | "sheet";

/**
 * What the frame is worth in each place. The rail's 600 is where the list runs
 * 628px with its sub-entries closed, so it clips 30px of it; the sheet clips at
 * 16rem instead, and pads tighter, because it is a bottom bar over the article.
 */
const CHROME = {
  rail: { pad: "16px", maxHeight: 600 },
  sheet: { pad: "11.2px", maxHeight: 256 },
} satisfies Record<ContentsVariant, { pad: string; maxHeight: number }>;

export default function ArticleContents({
  headings,
  variant = "rail",
}: {
  headings: Heading[];
  variant?: ContentsVariant;
}) {
  const sections = useMemo(() => toSections(headings), [headings]);
  const activeId = useActiveHeadingId(headings);
  // A reader who has asked for less motion gets the same rail, instantly.
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  // One id drives both tiers: the section holding the active heading stays lit
  // and open, whether the reader is in its own text or in a sub-section.
  const currentSectionId =
    sections.find(
      (section) =>
        section.heading.id === activeId ||
        section.children.some((child) => child.id === activeId)
    )?.heading.id ?? null;

  if (headings.length === 0) {
    return null;
  }

  return (
    <Stack component="nav" aria-label="In this article" rowGap={3}>
      {variant === "rail" ? (
        <Typography
          component="div"
          sx={{
            fontSize: 15,
            fontWeight: 500,
            lineHeight: "22.5px",
            letterSpacing: "1px",
            color: "primary.dark",
          }}
        >
          {TITLE}
        </Typography>
      ) : null}
      <Box
        sx={{
          // A grid, not a flex column: under `maxHeight` flex items shrink, and
          // a row that gives way cannot overflow the panel it is meant to fill.
          display: "grid",
          rowGap: "3.2px",
          p: CHROME[variant].pad,
          border: `1px solid ${marketingPalette.hairline}`,
          borderRadius: "8px",
          maxHeight: CHROME[variant].maxHeight,
          overflowY: "auto",
        }}
      >
        {sections.map((section) => {
          const current = section.heading.id === currentSectionId;
          return (
            <Box key={section.heading.id} sx={{ overflow: "hidden" }}>
              <SectionLink heading={section.heading} active={current} />
              {section.children.length > 0 ? (
                <Collapse
                  in={current}
                  timeout={reducedMotion ? 0 : EXPAND_MS}
                  easing={EXPAND_EASING}
                >
                  {section.children.map((child) => (
                    <Box
                      key={child.id}
                      sx={{
                        pl: 2.5,
                        borderLeft: `1px solid ${marketingPalette.wash}`,
                      }}
                    >
                      <SubsectionLink
                        heading={child}
                        active={child.id === activeId}
                      />
                    </Box>
                  ))}
                </Collapse>
              ) : null}
            </Box>
          );
        })}
      </Box>
    </Stack>
  );
}
